import { PayloadAction } from '@reduxjs/toolkit';

import { u21CreateSlice } from 'app/shared/thunk/u21CreateSlice';
import { EvaluationLog } from 'app/shared/eventsOld/models';
import { u21CreateAsyncThunk } from 'app/shared/thunk/u21CreateAsyncThunk';
import routes from 'app/shared/utils/routes';
import { get } from 'app/shared/utils/fetchr';
import {
  ActionEventExternalId,
  TxnEventExternalId,
  txnEventExternalId,
} from 'app/shared/types/eventExternalIds';
import { computeDlHashIdForCgdo } from 'app/shared/utils/dlHashId';
import { selectAgentOrgID } from 'app/modules/session/selectors';
import { selectHashedIdRoutesEnabled } from 'app/shared/featureFlags/selectors';

const EVENTS_NAME = 'sliceEvents';

const resolveHashedId = (state: RootState, externalId: string): string => {
  if (!selectHashedIdRoutesEnabled(state) || !externalId) return '';
  return computeDlHashIdForCgdo(selectAgentOrgID(state), externalId);
};

interface EventSliceState {
  evaluationLog: EvaluationLog;
  loadingEvaluationLog: boolean;
}

const initialState: Readonly<EventSliceState> = {
  evaluationLog: {
    evaluation_id: '',
    event_id: txnEventExternalId(''),
    rule_executions: {},
  },
  loadingEvaluationLog: false,
};

export const retrieveEvaluationLogThunk = u21CreateAsyncThunk(
  `${EVENTS_NAME}/RETRIEVE_TRANSACTION_EVALUATION_LOG`,
  async (
    {
      id,
      // Txn and action events resolve via different fast tables, so each has
      // its own cached route. Callers pass the event type so we hit the right
      // resolver; the hash is computed identically for both.
      eventType = 'txn',
    }: {
      id: TxnEventExternalId | ActionEventExternalId;
      eventType?: 'txn' | 'action';
    },
    { getState },
  ) => {
    try {
      const hashedId = resolveHashedId(getState(), id);
      // Cached routes are per-object-type (txn vs action fast tables). The
      // legacy route is shared: /txn-events/<external_id>/evaluation_log serves
      // both event types, so flag-off behavior is unchanged for either.
      const cachedResource =
        eventType === 'action' ? 'action-events' : 'txn-events';
      const path = hashedId
        ? `/v1/${cachedResource}/cached/${hashedId}/evaluation_log`
        : routes.patronus.txnEventsEvaluationLogEventId.replace(
            ':eventId',
            encodeURIComponent(id),
          );
      return await get(path);
    } catch {
      return initialState.evaluationLog;
    }
  },
);

const eventsSlice = u21CreateSlice({
  name: EVENTS_NAME,
  initialState,
  reducers: {
    clearEvaluationLog: (draft) => {
      draft.evaluationLog = initialState.evaluationLog;
    },
  },
  extraReducers: (builder) => {
    builder.addLoadingCase(
      retrieveEvaluationLogThunk,
      'loadingEvaluationLog',
      (draft, action: PayloadAction<EvaluationLog>) => {
        draft.evaluationLog = action.payload;
      },
    );
  },
});

export const EVENTS_SLICE_NAME = eventsSlice.name;
export default eventsSlice.reducer;
export const { name, reducer, actions } = eventsSlice;
export const { clearEvaluationLog } = actions;
